import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useState, useEffect, useRef } from "react";
import Header from "../../../components/Header";
import { loanManagementUrl, mpesaServiceUrls } from "../../../config";
import { executeAnyMutation } from "../../../data/DynamicServiceNew/jsondynamicservice";
import Swal from "sweetalert2";

const SABABISHA_RESULT_MESSAGES = {
  403: "No disbursement on record for this loan.",
  409: "Maker record already submitted for this loan.",
  423: "Safaricom confirmed this payment was successfully disbursed. Sababisha is not required.",
  424: "Safaricom registered the original request but no callback yet. Do not proceed with Sababisha yet.",
};

const MAX_POLL_ATTEMPTS = 4;
const POLL_INTERVAL_MS = 10000;

const getAuthHeader = () => {
  const raw = localStorage.getItem("token");
  return raw ? `Bearer ${raw.replace(/"/g, "")}` : "";
};
const loan_url = loanManagementUrl.uri;

const InitiateSababishaForm = (props) => {
  const [statusMessage, setStatusMessage] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollAttempt, setPollAttempt] = useState(0);

  const pendingLoanIdRef = useRef(null);
  const pollCountRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const stopPolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setPolling(false);
    pollCountRef.current = 0;
  };

  const callStatusCheck = (loanId) =>
    fetch(mpesaServiceUrls.sababishaStatusCheck, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
      body: JSON.stringify({ loanId: Number(loanId) }),
    });

  const callSababishaStatus = (loanId) =>
    fetch(`${mpesaServiceUrls.sababishaStatus}/${loanId}`, {
      headers: { Authorization: getAuthHeader() },
    });

  const handleStatusResponse = async (resp, loanId) => {
    if (resp.status === 409) {
      const body = await resp.json().catch(() => ({}));
      setBlocked(true);
      setStatusMessage({
        severity: "warning",
        text: "Payment confirmed paid. Sababisha is not required.",
        extra: body.transactionId
          ? `M-PESA Receipt: ${body.transactionId}`
          : null,
      });
      return;
    }

    if (resp.status === 201) {
      const body = await resp.json().catch(() => ({}));
      const code = body.sababishaResult;

      if (code !== undefined && code !== 200) {
        setBlocked(true);
        setStatusMessage({
          severity: "error",
          text:
            SABABISHA_RESULT_MESSAGES[code] ||
            `Operation blocked (code ${code}).`,
        });
        return;
      }

      const text = body.message;

      setStatusMessage({ severity: "success", text });
      props.onAction?.();
      return;
    }

    if (resp.status === 202) {
      startPolling(loanId);
      return;
    }

    if (resp.status === 404) {
      setStatusMessage({
        severity: "error",
        text: "No disbursement found for this loan ID.",
      });
      return;
    }

    if (resp.status === 422) {
      props.onClose();
      const confirm = await Swal.fire({
        title: "Payment Unconfirmed",
        text: "M-PESA payment was not confirmed. Do you still want to create the Sababisha record?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, create record",
        cancelButtonText: "No, cancel",
        confirmButtonColor: "#d33",
      });

      if (!confirm.isConfirmed) return;

      const variables = { p_flag: 0, p_loanid: loanId };

      const response = await executeAnyMutation(
        props.isEditing ? "" : "sababishaFunction",
        variables,
        "param",
        false,
        loan_url,
      );

      const parsedResponse = JSON.parse(response?.results);
      if (parsedResponse == "Success") {
        props.onAction();
        props.onClose();
        Swal.fire(
          "Success!",
          `Loan Disbursement  has been initiated successfully, Please approve it here in actions`,
          "success",
        );
      } else {
        props.onClose();
        Swal.fire("Error!", "Please contact the Admin.", "error");
      }
      return;
    }
    setStatusMessage({
      severity: "error",
      text: "Server error. Please try again or contact support.",
    });
  };

  const handlePollTick = async (loanId) => {
    pollCountRef.current += 1;
    setPollAttempt(pollCountRef.current);

    let body;
    try {
      const resp = await callSababishaStatus(loanId);
      body = await resp.json();
    } catch (e) {
      stopPolling();
      setStatusMessage({
        severity: "error",
        text: `Error checking status: ${e.message}`,
      });
      return;
    }

    if (body.status === "confirmed_paid") {
      stopPolling();
      setBlocked(true);
      const extra = [
        body.transactionId && `M-PESA Receipt: ${body.transactionId}`,
        body.amount != null &&
          `Amount: KES ${Number(body.amount).toLocaleString()}`,
        body.creditParty && `Recipient: ${body.creditParty}`,
      ]
        .filter(Boolean)
        .join(" · ");
      setStatusMessage({
        severity: "warning",
        text: "Payment confirmed paid. Sababisha is not required.",
        extra: extra || null,
      });
      return;
    }

    if (body.status === "confirmed_failed") {
      stopPolling();
      setStatusMessage({
        severity: "info",
        text:
          body.message ||
          "Payment confirmed failed.  Sababisha record has been created.",
        extra: `M-PESA Receipt: ${body.receiptNo}` || null,
      });

      return;
    }

    if (body.status === "unconfirmed") {
      stopPolling();

      const confirm = await Swal.fire({
        title: "Payment Unconfirmed",
        text: "M-PESA payment was not confirmed. Do you still want to create the Sababisha record?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, create record",
        cancelButtonText: "No, cancel",
        confirmButtonColor: "#d33",
      });

      if (!confirm.isConfirmed) return;

      const variables = { p_flag: 0, p_loanid: loanId };

      const response = await executeAnyMutation(
        props.isEditing ? "" : "sababishaFunction",
        variables,
        "param",
        false,
        loan_url,
      );

      const parsedResponse = JSON.parse(response?.results);
      if (parsedResponse == "Success") {
        props.onAction();
        props.onClose();
        Swal.fire(
          "Success!",
          `Loan Disbursement  has been initiated successfully, Please approve it here in actions`,
          "success",
        );
      } else {
        props.onClose();
        Swal.fire("Error!", "Please contact the Admin.", "error");
      }
      return;
    }
    // "pending" — keep waiting unless we've exhausted attempts
    if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
      stopPolling();
      setStatusMessage({
        severity: "warning",
        text: "No callback received from Safaricom after 40 seconds. Please try again in a few minutes.",
      });
    }
  };

  const startPolling = (loanId) => {
    pendingLoanIdRef.current = loanId;
    pollCountRef.current = 0;
    setPollAttempt(0);
    setPolling(true);
    setStatusMessage({
      severity: "info",
      text: "Status query sent to Safaricom. Checking every 10 seconds…",
    });
    timerRef.current = setInterval(() => {
      handlePollTick(pendingLoanIdRef.current);
    }, POLL_INTERVAL_MS);
  };

  const initialValues = { loanId: "" };

  const validationSchema = yup.object().shape({
    loanId: yup
      .number()
      .typeError("Loan ID must be a number")
      .required("Loan ID is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setStatusMessage(null);
    setBlocked(false);
    stopPolling();
    setPollAttempt(0);
    try {
      const resp = await callStatusCheck(values.loanId);
      await handleStatusResponse(resp, values.loanId);
    } catch (e) {
      setStatusMessage({
        severity: "error",
        text: `Unexpected error: ${e.message}`,
      });
    }
    setSubmitting(false);
  };

  return (
    <Box m="20px">
      <Header
        title="Initiate Disbursement Sababisha"
        subtitle="Verify disbursement status before retrying"
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="30px">
              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Loan ID *"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.loanId}
                name="loanId"
                error={touched.loanId && !!errors.loanId}
                helperText={touched.loanId && errors.loanId}
                sx={{ gridColumn: "span 2" }}
                disabled={polling || blocked}
              />

              {statusMessage && (
                <Alert
                  severity={statusMessage.severity}
                  sx={{ gridColumn: "span 2" }}
                >
                  {statusMessage.text}
                  {statusMessage.extra && (
                    <Typography variant="body2" mt={0.5}>
                      {statusMessage.extra}
                    </Typography>
                  )}
                </Alert>
              )}

              {polling && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{ gridColumn: "span 2" }}
                >
                  <CircularProgress size={20} />
                  <Typography>
                    {pollAttempt === 0
                      ? "Waiting… first check in 10 seconds"
                      : `Attempt ${pollAttempt} of ${MAX_POLL_ATTEMPTS} — checking Safaricom status`}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box display="flex" justifyContent="end" mt="20px" gap={1}>
              <Button
                variant="outlined"
                onClick={props.onClose}
                disabled={isSubmitting || polling}
              >
                Close
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting || polling || blocked}
                startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
              >
                {isSubmitting ? "Checking…" : "Check & Initiate"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default InitiateSababishaForm;
